interface DotNavigationMapProps {
  numItems: number;
  currentItem: number;
  maxNumDotsToShow?: number;
}

/**
 * Component used as a navigation map for page indexing in
 * carousel displays
 * @param numItems - Total number of items used in this carousel.
 * @param currentItem - Variable that indicates the index of the current item selected.
 * @param maxNumDotsToShow - (Optional) Parameter that defines the number of dots to be shown in this map.
 * @returns
 */
const DotNavigationMap: React.FC<DotNavigationMapProps> = ({
  numItems,
  currentItem,
  maxNumDotsToShow = 7,
}) => {
  // Definition of the window span
  const windowSpan = Math.floor(maxNumDotsToShow / 2);

  // Definition of the window borders
  const windowStart = Math.max(
    0,
    Math.min(numItems - maxNumDotsToShow, currentItem - windowSpan) // This define the lower limit when starting
  );
  const windowEnd = Math.min(
    Math.max(maxNumDotsToShow - 1, currentItem + windowSpan),
    numItems - 1
  );

  // Calculate transform offset for sliding effect
  const dotWidth = 8; // This matches the width of active dots (w-8 = 2rem = 32px)
  const dotSpacing = 8; // This matches the gap-2 (2 * 4px = 8px)
  const translateX = -(windowStart * (dotWidth + dotSpacing));

  return (
    <div
      className="overflow-hidden inline-flex justify-start items-center"
      // Applying min to prevent expanding up to `maxNumDotsToShow` width
      // when there's less item than this number
      style={{ width: 32 + 16 * (Math.min(maxNumDotsToShow, numItems) - 1) }}
    >
      <div
        className="justify-center items-center gap-2 inline-flex transition-transform duration-300 ease-in-out"
        style={{ transform: `translateX(${translateX}px)` }}
      >
        {[...Array(numItems).keys()].map((dot: number) => {
          // Define the possible states for the current dot
          const isActive = dot === currentItem;
          const isPast = dot < currentItem;
          const isFuture = dot > currentItem;
          const isFirstDot = dot === windowStart && windowStart > 0;
          const isLastDot = dot === windowEnd && windowEnd < numItems - 1;

          return (
            <div
              key={dot}
              className={`
               transform transition-all duration-300 ease-in-out
                ${isActive ? "w-8 h-2 bg-neutral-400" : ""}
                ${
                  isPast
                    ? `${isFirstDot ? "w-1.5 h-1.5" : "w-2 h-2"} bg-neutral-400`
                    : ""
                }
                ${
                  isFuture
                    ? `${isLastDot ? "w-1.5 h-1.5" : "w-2 h-2"} bg-neutral-200`
                    : ""
                }
                rounded-xl
              `}
            />
          );
        })}
      </div>
    </div>
  );
};

export default DotNavigationMap;
